import React, { useMemo } from "react";
import { CompassParagraph } from "../styledComponents";
import {
  ElementContainer,
  HorizontalLine,
  RowContainer,
  FlexibleContainer,
} from "../../containers/styledComponents";
import {
  BatchAssessmentResult,
  PillarPassRates,
  SinDetectionCounts,
} from "../../../types/compassAITypes";
import { MESSAGES } from "../../../messages";
import successAnimation from "../../../assets/lottie/success_checkmark_animation.json";
import {
  compassDarBlue,
  compassLightGreen,
  compassYellow,
  brightRed,
  greyText,
  graphPaletteV2,
} from "../../../styles";
import Lottie from "react-lottie-player";
import { IconContainer, CircleTag } from "../../visuals/styledComponents";
import { KPIBox } from "../../visuals/KPIBox";
import { DataEntry } from "../../../models/stewardshipModels";
import { CompactDonutChart } from "../../charts/CompactDonutChart";
import { CompactBarChart } from "../../charts/CompactBarChart";
import { CompassProgressBar } from "../CompassProgressBar";

import calculatorIcon from "../../../assets/svg/metrics_icon.svg";

// Light compass tag background colors
const LIGHT_GREEN_BG = "#DDF6E8";
const LIGHT_YELLOW_BG = "#FFF6D9";
const LIGHT_RED_BG = "#fce4ec";

// =============================================================================
// HELPERS
// =============================================================================

const PILLAR_LABELS: Record<keyof PillarPassRates, string> = {
  p1_intent: "Intent",
  p2_reach: "Reach",
  p3_approach: "Approach",
  p4_persistence: "Persistence",
  p5_resonance: "Resonance",
  p6_influence: "Influence",
};

const PILLAR_INITIALS: Record<keyof PillarPassRates, string> = {
  p1_intent: "I",
  p2_reach: "Re",
  p3_approach: "A",
  p4_persistence: "P",
  p5_resonance: "Rs",
  p6_influence: "In",
};

const SIN_LABELS: Record<keyof SinDetectionCounts, string> = {
  s1_irrelevance: "Irrelevance",
  s2_opaque_objectives: "Opaque Objectives",
  s3_misattribution: "Misattribution",
  s4_stale_engagements: "Stale Engagements",
  s5_unverifiable_outcomes: "Unverifiable Outcomes",
  s6_mixed_signals: "Mixed Signals",
  s7_no_strategic_context: "No Strategic Context",
};

const getPassRateColor = (rate: number): string =>
  rate >= 0.7 ? "#2e7d32" : rate >= 0.4 ? "#f57f17" : "#c62828";

const getPassRateBg = (rate: number): string =>
  rate >= 0.7 ? LIGHT_GREEN_BG : rate >= 0.4 ? LIGHT_YELLOW_BG : LIGHT_RED_BG;

const getSinBarColor = (rate: number): string =>
  rate <= 0.1 ? compassLightGreen : rate <= 0.3 ? compassYellow : brightRed;

// =============================================================================
// TYPES
// =============================================================================

export interface AssessmentResultsSummaryProps {
  result: BatchAssessmentResult;
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const PillarCircle: React.FC<{
  pillarKey: keyof PillarPassRates;
  passCount: number;
  total: number;
}> = ({ pillarKey, passCount, total }) => {
  const rate = total > 0 ? passCount / total : 0;
  const color = getPassRateColor(rate);
  const bg = getPassRateBg(rate);
  const pct = total > 0 ? `${Math.round(rate * 100)}%` : "—";
  const initial = PILLAR_INITIALS[pillarKey];
  const label = PILLAR_LABELS[pillarKey];

  return (
    <FlexibleContainer fd="column" ai="center" style={{ gap: 2, minWidth: 52 }}>
      <CircleTag color={bg} textColor={color} size={34}>
        {initial}
      </CircleTag>
      <CompassParagraph fontSize={13} color={color}>
        {pct}
      </CompassParagraph>
      <CompassParagraph
        fontSize={11}
        color={greyText}
        style={{ textAlign: "center" }}
      >
        {label}
      </CompassParagraph>
    </FlexibleContainer>
  );
};

const SinProgressRow: React.FC<{
  label: string;
  count: number;
  total: number;
}> = ({ label, count, total }) => {
  const rate = total > 0 ? count / total : 0;
  const pct = Math.round(rate * 100);

  return (
    <FlexibleContainer ai="center" style={{ gap: 10 }} p="1px 0">
      <CompassParagraph
        fontSize={12}
        color={greyText}
        style={{ minWidth: 140, margin: 0 }}
      >
        {label}
      </CompassParagraph>
      <FlexibleContainer style={{ flex: 1 }}>
        <CompassProgressBar
          value={count}
          max={total || 1}
          height={6}
          color={getSinBarColor(rate)}
        />
      </FlexibleContainer>
      <CompassParagraph
        fontSize={12}
        color={greyText}
        style={{ minWidth: 36, textAlign: "right", margin: 0 }}
      >
        {pct}%
      </CompassParagraph>
    </FlexibleContainer>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const AssessmentResultsSummary: React.FC<
  AssessmentResultsSummaryProps
> = ({ result }) => {
  const messages = MESSAGES.COMPASS_AI.ENGAGEMENT_ASSESSMENT.SUMMARY;

  const totalSinsDetected = useMemo(
    () => Object.values(result.sin_detection_counts).reduce((a, b) => a + b, 0),
    [result.sin_detection_counts],
  );

  const pillarBarData = useMemo(
    () =>
      (Object.keys(PILLAR_LABELS) as (keyof PillarPassRates)[])
        .map((key) => ({
          label: PILLAR_LABELS[key],
          value:
            result.successful > 0
              ? Math.round(
                  (result.pillar_pass_rates[key] / result.successful) * 100,
                )
              : 0,
        }))
        .sort((a, b) => b.value - a.value),
    [result.pillar_pass_rates, result.successful],
  );

  const sinBarData = useMemo(
    () =>
      (Object.keys(SIN_LABELS) as (keyof SinDetectionCounts)[])
        .map((key) => ({
          label: SIN_LABELS[key],
          value: result.sin_detection_counts[key],
        }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value),
    [result.sin_detection_counts],
  );

  return (
    <>
      {/* ── Success Header ──────────────────────────────────── */}
      <RowContainer jc="flex-start" ai="center" ml={-6}>
        <IconContainer size={42} m={0} p={0}>
          <Lottie loop={false} play animationData={successAnimation} />
        </IconContainer>
        <ElementContainer flexDirection="column">
          <CompassParagraph color={compassDarBlue} fontSize={14} bold>
            {MESSAGES.COMPASS_AI.ENGAGEMENT_ASSESSMENT.DESCRIPTION.SUCCESS}
          </CompassParagraph>
        </ElementContainer>
      </RowContainer>
      <HorizontalLine marginTop={4} />

      {/* ── KPIs ────────────────────────────────────────────── */}
      <KPIBox
        icon={calculatorIcon}
        title="Assessment Results"
        kpis={
          [
            { label: messages.TOTAL_ASSESSED, value: result.total_records },
            { label: messages.SUCCESSFUL, value: result.successful },
            { label: messages.FAILED, value: result.failed },
            { label: messages.SINS_DETECTED, value: totalSinsDetected },
          ] as DataEntry<number>[]
        }
      />

      {/* ── Failed records note ─────────────────────────────── */}
      {result.failed > 0 && (
        <RowContainer jc="flex-start" mt={4}>
          <CompassParagraph fontSize={13} color={brightRed}>
            {result.failed} record{result.failed !== 1 ? "s" : ""} failed
          </CompassParagraph>
        </RowContainer>
      )}

      {/* ── Donut + Pillar Circles side by side ─────────────── */}
      <RowContainer jc="flex-start" mt={10} mb={2}>
        <CompassParagraph fontSize={14} bold>
          Gateway Distribution
        </CompassParagraph>
      </RowContainer>
      <FlexibleContainer
        jc="flex-start"
        ai="flex-start"
        fw="wrap"
        style={{ gap: 16 }}
      >
        <CompactDonutChart
          data={[
            { label: "Genuine", value: result.gateway_verdicts.Genuine },
            { label: "Unclear", value: result.gateway_verdicts.Unclear },
            { label: "Hidden", value: result.gateway_verdicts.Hidden },
          ].filter((d) => d.value > 0)}
          colors={[compassLightGreen, compassYellow, brightRed]}
          size={160}
          containerHeight={160}
        />
        <FlexibleContainer fd="column" ai="flex-start" style={{ gap: 4 }}>
          <CompassParagraph fontSize={13} bold mb={2}>
            Pillar Pass Rates
          </CompassParagraph>
          <FlexibleContainer
            jc="flex-start"
            ai="flex-start"
            fw="wrap"
            style={{ gap: 8 }}
          >
            {(Object.keys(PILLAR_LABELS) as (keyof PillarPassRates)[]).map(
              (key) => (
                <PillarCircle
                  key={key}
                  pillarKey={key}
                  passCount={result.pillar_pass_rates[key]}
                  total={result.successful}
                />
              ),
            )}
          </FlexibleContainer>
        </FlexibleContainer>
      </FlexibleContainer>

      {/* ── Charts side by side ─────────────────────────────── */}
      {(pillarBarData.length > 0 || sinBarData.length > 0) && (
        <FlexibleContainer
          jc="flex-start"
          ai="flex-start"
          style={{ gap: 20 }}
          mt="12px"
        >
          {pillarBarData.length > 0 && (
            <ElementContainer
              flexDirection="column"
              style={{ flex: 1, minWidth: 0 }}
            >
              <CompassParagraph fontSize={13} bold mb={4}>
                Pillar Pass Rates (%)
              </CompassParagraph>
              <CompactBarChart
                data={pillarBarData}
                colors={graphPaletteV2}
                height={110}
                width="90px"
                containerHeight={130}
              />
            </ElementContainer>
          )}
          {sinBarData.length > 0 && (
            <ElementContainer
              flexDirection="column"
              style={{ flex: 1, minWidth: 0 }}
            >
              <CompassParagraph fontSize={13} bold mb={4}>
                Sins Detected
              </CompassParagraph>
              <CompactBarChart
                data={sinBarData}
                colors={[
                  "#9A9CEA",
                  "#A2DCEE",
                  "#74B8EF",
                  "#BBEEED",
                  "#0B759D",
                  "#A5FFD6",
                  "#E9E7FD",
                ]}
                height={110}
                width="90px"
                containerHeight={130}
              />
            </ElementContainer>
          )}
        </FlexibleContainer>
      )}

      {/* ── Sin Detection (progress bars) ───────────────────── */}
      <RowContainer jc="flex-start" mt={10} mb={2}>
        <CompassParagraph fontSize={14} bold>
          Sin Detection
        </CompassParagraph>
      </RowContainer>
      {(Object.keys(SIN_LABELS) as (keyof SinDetectionCounts)[]).map((key) => (
        <SinProgressRow
          key={key}
          label={SIN_LABELS[key]}
          count={result.sin_detection_counts[key]}
          total={result.successful}
        />
      ))}
    </>
  );
};
